import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { moduleApi, authApi } from '../../lib/api';
import { Briefcase, Plus, Trash2, Users, ChevronRight, Save, X, Search, ClipboardList, Send } from 'lucide-react';
import { DocumentTemplatesSettings } from '../Settings/DocumentTemplatesSettings';
import { SendSurveyToEmployeeModal } from './SendSurveyToEmployeeModal';
import { EmployeeSurveyRequestsSettings } from './EmployeeSurveyRequestsSettings';
import { OnboardingTemplatesSettings } from './OnboardingTemplatesSettings';
import { Button } from '../ui/Button';

type TabKey = 'posts' | 'employees' | 'surveys' | 'onboarding';

interface JobPost {
    id: string;
    title: string;
}

interface EmployeeDocument {
    id?: string;
    display_name: string;
    file_name: string;
    mime_type?: string;
    file_size?: number;
    storage_key?: string;
}

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    address?: string;
    position: string;
    job_post_title?: string;
    documents?: EmployeeDocument[];
    is_account_active?: number | boolean;
}

const tabButton = (active: boolean) =>
    `px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${active ? 'bg-brand-600 text-brand-fg shadow-sm' : 'text-[var(--text-muted)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'}`;

export const PostesEmployesModule = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabKey>('posts');
    const [loading, setLoading] = useState(true);

    const [jobPosts, setJobPosts] = useState<JobPost[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [activeModules, setActiveModules] = useState<string[]>([]);

    const [showPostModal, setShowPostModal] = useState(false);
    const [editingPost, setEditingPost] = useState<any>(null);
    const [newPostTitle, setNewPostTitle] = useState('');
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);

    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        position: '',
        email: '',
        phone: '',
        address: ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [surveyTargetEmployee, setSurveyTargetEmployee] = useState<Employee | null>(null);
    const [activatePortalOnCreate, setActivatePortalOnCreate] = useState(false);
    const [onboardingTemplates, setOnboardingTemplates] = useState<{ id: string; name: string }[]>([]);
    const [assignOnboardingId, setAssignOnboardingId] = useState('');

    useEffect(() => {
        loadData();
        authApi.getMyModules().then((modules: any) => {
            const names = (Array.isArray(modules) ? modules : [])
                .filter((m: any) => m.is_active === 1)
                .map((m: any) => m.module_name);
            setActiveModules(names);
        }).catch((e) => console.error('Failed to load active modules', e));
        moduleApi.getOnboardingTemplates()
            .then((d: any) => setOnboardingTemplates((Array.isArray(d) ? d : []).filter((t: any) => t.is_active)))
            .catch(() => setOnboardingTemplates([]));
    }, []);

    const showAccountStatus = activeModules.includes('portail_salarie');
    // Sondages, onboarding, notes de frais et pointage n'ont de sens que via le portail salarié —
    // masqués si ce module n'est pas actif (l'API les refuse aussi côté serveur, pas juste ici).
    const hasPortailSalarie = activeModules.includes('portail_salarie');

    useEffect(() => {
        if (!hasPortailSalarie && ['surveys', 'onboarding'].includes(activeTab)) {
            setActiveTab('posts');
        }
    }, [hasPortailSalarie, activeTab]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [posts, emps] = await Promise.all([moduleApi.getJobPosts(), moduleApi.getEmployes()]);
            setJobPosts(posts || []);
            setEmployees(emps || []);
        } catch (e: any) {
            setError(e?.message || 'Erreur de chargement.');
        } finally {
            setLoading(false);
        }
    };

    const employeesByPost = useMemo(() => {
        const map: Record<string, number> = {};
        employees.forEach((emp) => {
            map[emp.position] = (map[emp.position] || 0) + 1;
        });
        return map;
    }, [employees]);

    const filteredEmployees = useMemo(() => {
        const term = employeeSearch.trim().toLowerCase();
        let result = employees;
        if (term) {
            result = employees.filter((employee) => {
                const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.toLowerCase();
                const email = String(employee.email || '').toLowerCase();
                const phone = String(employee.phone || '').toLowerCase();
                const post = String(employee.job_post_title || '').toLowerCase();
                return fullName.includes(term) || email.includes(term) || phone.includes(term) || post.includes(term);
            });
        }
        return [...result].sort((a, b) => {
            const nameA = `${a.last_name || ''} ${a.first_name || ''}`.trim().toUpperCase();
            const nameB = `${b.last_name || ''} ${b.first_name || ''}`.trim().toUpperCase();
            return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
    }, [employees, employeeSearch]);

    const resetForm = () => {
        setForm({ first_name: '', last_name: '', position: '', email: '', phone: '', address: '' });
        setActivatePortalOnCreate(false);
        setAssignOnboardingId('');
        setError('');
    };

    const openCreateEmployee = () => {
        resetForm();
        setShowEmployeeModal(true);
    };

    const openPostModal = (post: any = null) => {
        setEditingPost(post);
        setNewPostTitle(post ? post.title : '');
        setError('');
        setShowPostModal(true);
    };

    const addPost = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPostTitle.trim()) return;
        try {
            if (editingPost) {
                // Si une API de mise à jour existe, sinon on supprime/recrée ou on laisse tomber pour l'instant
                // Pour l'instant, l'utilisateur a demandé d'AJOUTER.
                // On va se concentrer sur l'ajout.
            } else {
                await moduleApi.createJobPost({ title: newPostTitle.trim() });
            }
            setNewPostTitle('');
            setShowPostModal(false);
            setEditingPost(null);
            await loadData();
        } catch (err: any) {
            console.error('Failed to create job post:', err);
            setError(err?.message || 'Impossible de créer le poste.');
        }
    };

    const removePost = async (id: string) => {
        if (!confirm('Supprimer ce poste ?')) return;
        try {
            await moduleApi.deleteJobPost(id);
            await loadData();
        } catch (err: any) {
            setError(err?.message || 'Impossible de supprimer ce poste.');
        }
    };

    const saveEmployee = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!form.first_name.trim() || !form.last_name.trim() || !form.position) {
            setError('Nom, prénom et poste sont obligatoires.');
            return;
        }
        if (activatePortalOnCreate && !form.email.trim()) {
            setError('Une adresse email est requise pour activer le compte à la création.');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                address: form.address.trim()
            };

            const res: any = await moduleApi.createEmploye(payload);

            // Activation du compte + assignation d'un parcours à la création : deux actions distinctes déjà
            // possibles séparément depuis la fiche salarié, ici enchaînées pour éviter l'aller-retour. Un
            // échec de l'une ou l'autre ne doit jamais annuler la création du salarié, déjà actée à ce stade.
            if (res?.id && activatePortalOnCreate) {
                try { await moduleApi.activateEmployeePortal(res.id); } catch (err: any) { setError(err?.message || 'Salarié créé, mais l\'activation du compte a échoué.'); }
            }
            if (res?.id && assignOnboardingId) {
                try { await moduleApi.assignOnboarding(res.id, assignOnboardingId); } catch (err: any) { setError(err?.message || 'Salarié créé, mais l\'assignation du parcours a échoué.'); }
            }

            setShowEmployeeModal(false);
            resetForm();
            await loadData();
        } catch (err: any) {
            setError(err?.message || 'Impossible d\'enregistrer l\'employé.');
        } finally {
            setSaving(false);
        }
    };

    const deleteEmployee = async (id: string) => {
        if (!confirm('Supprimer cet employé ?')) return;
        try {
            await moduleApi.deleteEmploye(id);
            await loadData();
        } catch (err: any) {
            setError(err?.message || 'Impossible de supprimer cet employé.');
        }
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold tracking-tight text-content">Postes & Employés</h1>
                    <p className="text-content-muted text-sm mt-0.5">Gestion RH complète: postes, profils collaborateurs et documents.</p>
                </div>
                {activeTab === 'posts' && (
                    <Button onClick={() => openPostModal()} className="self-start sm:self-auto">
                        <Plus size={15} /> Ajouter un poste
                    </Button>
                )}
            </header>

            <div className="bg-white dark:bg-surface border border-border rounded-2xl p-2 inline-flex gap-2 transition-colors duration-200">
                <button className={tabButton(activeTab === 'posts')} onClick={() => setActiveTab('posts')}>
                    Gestion des Postes
                </button>
                <button className={tabButton(activeTab === 'employees')} onClick={() => setActiveTab('employees')}>
                    Liste des Employés
                </button>
                {hasPortailSalarie && (
                    <>
                        <button className={tabButton(activeTab === 'surveys')} onClick={() => setActiveTab('surveys')}>
                            Sondages
                        </button>
                        <button className={tabButton(activeTab === 'onboarding')} onClick={() => setActiveTab('onboarding')}>
                            Onboarding
                        </button>
                    </>
                )}
            </div>

            {error && <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

            {activeTab === 'posts' && (
                <section className="space-y-6">
                    {/* Le formulaire est maintenant dans une modale pour plus de clarté */}

                    <div className="bg-white dark:bg-surface border border-border rounded-2xl overflow-x-auto transition-colors duration-200">
                        <table className="w-full text-left min-w-[500px]">
                            <thead className="bg-slate-100 dark:bg-black/60 border-b border-border">
                                <tr>
                                    <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Poste</th>
                                    <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Employés liés</th>
                                    <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                                {!loading && jobPosts.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-12 text-center text-slate-500 dark:text-content-muted">Aucun poste créé.</td>
                                    </tr>
                                )}
                                {jobPosts.map((post) => (
                                    <tr key={post.id} className="hover:bg-brand-600/10 transition-colors duration-200">
                                        <td className="px-6 py-4 text-slate-900 dark:text-content font-medium">{post.title}</td>
                                        <td className="px-6 py-4 text-slate-700 dark:text-content-muted">{employeesByPost[post.id] || 0}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button onClick={() => removePost(post.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {activeTab === 'employees' && (
                <section className="space-y-6">
                    <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                        <div className="relative w-full md:max-w-sm">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-content-muted" />
                            <input
                                value={employeeSearch}
                                onChange={(e) => setEmployeeSearch(e.target.value)}
                                placeholder="Rechercher un employé, email, téléphone, poste..."
                                className="w-full bg-surface border border-border rounded-xl pl-9 pr-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border text-sm transition-colors duration-200"
                            />
                        </div>
                        <button onClick={openCreateEmployee} className="px-5 py-3 rounded-xl bg-brand-600 text-white font-bold hover:bg-brand-700 inline-flex items-center gap-2 transition-colors duration-200 shadow-sm">
                            <Plus size={16} /> Ajouter un employé
                        </button>
                    </div>

                    <div className="bg-white dark:bg-surface border border-border rounded-2xl overflow-x-auto transition-colors duration-200">
                        <table className="w-full text-left min-w-[800px]">
                            <thead className="bg-slate-100 dark:bg-black/60 border-b border-border">
                                <tr>
                                    <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Employé</th>
                                    <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Poste</th>
                                    <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Contact</th>
                                    <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Documents</th>
                                    {showAccountStatus && <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Compte</th>}
                                    <th className="px-6 py-4 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                                {!loading && filteredEmployees.length === 0 && (
                                    <tr>
                                        <td colSpan={showAccountStatus ? 6 : 5} className="px-6 py-12 text-center text-slate-500 dark:text-content-muted">
                                            {employeeSearch.trim() ? 'Aucun résultat pour cette recherche.' : 'Aucun employé enregistré.'}
                                        </td>
                                    </tr>
                                )}
                                {filteredEmployees.map((employee) => (
                                    <tr key={employee.id} className="hover:bg-brand-600/10 transition-colors duration-200">
                                        <td className="px-6 py-4">
                                            <p className="text-slate-900 dark:text-content font-medium">
                                                {(employee.last_name || '').toUpperCase()} {employee.first_name || ''}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4 text-slate-800 dark:text-content-muted">{employee.job_post_title || '-'}</td>
                                        <td className="px-6 py-4 text-slate-700 dark:text-content-muted text-sm">
                                            <div>{employee.email || '-'}</div>
                                            <div>{employee.phone || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-800 dark:text-content-muted">{employee.documents?.length || 0}</td>
                                        {showAccountStatus && (
                                            <td className="px-6 py-4">
                                                {employee.is_account_active ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-xs font-bold">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Activé
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-500/10 text-slate-500 dark:text-content-muted border border-slate-500/20 text-xs font-bold">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Non activé
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-6 py-4">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => employee.email && setSurveyTargetEmployee(employee)} disabled={!employee.email}
                                                    title={employee.email ? 'Envoyer un sondage' : 'Aucune adresse email renseignée'}
                                                    className="p-2 rounded-lg text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors duration-200">
                                                    <Send size={16} />
                                                </button>
                                                <button onClick={() => navigate(`/employes/${employee.id}`)} title="Ouvrir la fiche du salarié" className="p-2 rounded-lg text-slate-700 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-slate-200 dark:bg-surface-soft transition-colors duration-200">
                                                    <ChevronRight size={16} />
                                                </button>
                                                <button onClick={() => deleteEmployee(employee.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {hasPortailSalarie && activeTab === 'surveys' && (
                <section className="space-y-8">
                    <DocumentTemplatesSettings audience="employe" />
                    <div>
                        <h2 className="text-lg font-bold text-slate-900 dark:text-content flex items-center gap-2 mb-3"><ClipboardList size={18} /> Sondages envoyés</h2>
                        <EmployeeSurveyRequestsSettings />
                    </div>
                </section>
            )}

            {hasPortailSalarie && activeTab === 'onboarding' && (
                <section>
                    <OnboardingTemplatesSettings />
                </section>
            )}

            {surveyTargetEmployee && (
                <SendSurveyToEmployeeModal employee={surveyTargetEmployee} onClose={() => setSurveyTargetEmployee(null)} onSent={() => setSurveyTargetEmployee(null)} />
            )}

            {/* Modal Poste */}
            <AnimatePresence>
                {showPostModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
                        >
                            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-content">
                                    {editingPost ? 'Modifier le poste' : 'Ajouter un nouveau poste'}
                                </h3>
                                <button onClick={() => setShowPostModal(false)} className="text-content-muted hover:text-gray-700 dark:hover:text-slate-900 transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                            <form onSubmit={addPost} className="p-6 space-y-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Intitulé du poste</label>
                                    <input
                                        autoFocus
                                        value={newPostTitle}
                                        onChange={(e) => setNewPostTitle(e.target.value)}
                                        placeholder="ex: Barman, Manager, Hôtesse..."
                                        className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200"
                                    />
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowPostModal(false)}
                                        className="flex-1 px-4 py-3 rounded-xl border border-border text-slate-700 dark:text-content-muted font-bold hover:bg-gray-50 dark:hover:bg-surface-soft transition-colors"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!newPostTitle.trim()}
                                        className="flex-1 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold hover:opacity-90 disabled:opacity-50 transition-colors"
                                    >
                                        {editingPost ? 'Mettre à jour' : 'Confirmer l\'ajout'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showEmployeeModal && (
                    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.96 }}
                            className="w-full max-w-3xl bg-white dark:bg-surface border border-border rounded-3xl shadow-2xl overflow-hidden transition-colors duration-200"
                        >
                            <div className="p-6 bg-surface-soft border-b border-border flex items-center justify-between transition-colors duration-200">
                                <h2 className="text-xl font-bold text-slate-900 dark:text-content">Nouvel Employé</h2>
                                <button onClick={() => setShowEmployeeModal(false)} className="p-2 rounded-lg text-slate-500 dark:text-content-muted hover:bg-slate-200 dark:hover:bg-surface-soft transition-colors duration-200">
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={saveEmployee} className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-xs uppercase tracking-widest text-slate-600 dark:text-content-muted font-bold">Prénom *</label>
                                        <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs uppercase tracking-widest text-slate-600 dark:text-content-muted font-bold">Nom *</label>
                                        <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs uppercase tracking-widest text-slate-600 dark:text-content-muted font-bold">Poste *</label>
                                        <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200">
                                            <option value="">Sélectionner un poste</option>
                                            {jobPosts.map((post) => (
                                                <option key={post.id} value={post.id}>{post.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs uppercase tracking-widest text-slate-600 dark:text-content-muted font-bold">Adresse mail</label>
                                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs uppercase tracking-widest text-slate-600 dark:text-content-muted font-bold">Téléphone</label>
                                        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200" />
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <label className="text-xs uppercase tracking-widest text-slate-600 dark:text-content-muted font-bold">Adresse postale</label>
                                        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-surface border border-border rounded-xl px-4 py-3 text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200" />
                                    </div>
                                </div>

                                {showAccountStatus && (
                                    <div className="p-4 rounded-xl bg-surface-soft border border-border space-y-3">
                                        <label className="flex items-center gap-2.5 cursor-pointer">
                                            <input type="checkbox" checked={activatePortalOnCreate} onChange={(e) => { setActivatePortalOnCreate(e.target.checked); if (!e.target.checked) setAssignOnboardingId(''); }} className="accent-brand-600 w-4 h-4" />
                                            <span className="text-sm font-bold text-slate-900 dark:text-content">Activer le compte du salarié dès la création</span>
                                        </label>
                                        <p className="text-xs text-slate-500 dark:text-content-muted ml-6">Un identifiant et un mot de passe temporaire lui sont envoyés par email — nécessite une adresse email ci-dessus.</p>
                                        {activatePortalOnCreate && onboardingTemplates.length > 0 && (
                                            <div className="ml-6 space-y-1.5">
                                                <label className="text-xs uppercase tracking-widest text-slate-600 dark:text-content-muted font-bold">Assigner un parcours d'intégration (optionnel)</label>
                                                <select value={assignOnboardingId} onChange={(e) => setAssignOnboardingId(e.target.value)} className="w-full bg-surface border border-border rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-content outline-none focus:border-slate-400 dark:focus:border-border transition-colors duration-200">
                                                    <option value="">Aucun</option>
                                                    {onboardingTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                </select>
                                                <p className="text-[11px] text-slate-500 dark:text-content-muted">S'il est choisi, le salarié y sera dirigé directement lors de sa première connexion.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <p className="text-xs text-slate-500 dark:text-content-muted">Les documents se gèrent depuis la fiche du salarié, après sa création.</p>

                                <div className="flex justify-end gap-3 pt-2">
                                    <button type="button" onClick={() => setShowEmployeeModal(false)} className="px-5 py-3 rounded-xl border border-border text-slate-900 dark:text-content font-bold hover:bg-slate-100 dark:hover:bg-surface-soft transition-colors duration-200">
                                        Annuler
                                    </button>
                                    <button disabled={saving} className="px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-60 transition-colors duration-200">
                                        <Save size={15} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};
